import React, { useEffect, useRef, useState } from 'react';
import api from '../utils/api';

// Field order and groups follow TenantRequestDetailsDTO (49 request fields).
export const registrationSections = [
  { title: 'Organization Details', fields: [
    ['organizationName', 'Organization Name', { required: true }],
    ['tradeName', 'Trade Name'],
    ['natureOfBusiness', 'Nature of Business'],
    ['industry', 'Industry'],
    ['productsServicesOffered', 'Products / Services Offered', { multiline: true }],
    ['yearOfEstablishment', 'Year of Establishment', { type: 'number', min: 1800, max: new Date().getFullYear() }],
    ['constitutionOfBusiness', 'Constitution of Business', { required: true, choices: ['Proprietorship', 'Partnership', 'LLP', 'Private Limited', 'Public Limited', 'Others'] }],
    ['website', 'Website'],
    ['companyLogoUrl', 'Company Logo URL'],
  ] },
  { title: 'GST & Tax Details', fields: [
    ['gstin', 'GSTIN', { maxLength: 15, pattern: '[0-9A-Z]{15}', title: 'Enter exactly 15 uppercase letters or digits.' }],
    ['pan', 'PAN', { maxLength: 10, pattern: '[A-Z]{5}[0-9]{4}[A-Z]', title: 'Enter 5 uppercase letters, 4 digits, and 1 uppercase letter.' }],
    ['udyamRegistrationNumber', 'Udyam Registration Number', { maxLength: 20 }],
    ['cinLlpin', 'CIN / LLPIN', { maxLength: 21 }],
    ['tan', 'TAN', { maxLength: 10 }],
    ['gstRegistrationType', 'GST Registration Type'],
    ['gstState', 'GST State'],
  ] },
  { title: 'Contact Information', fields: [
    ['registeredOfficeAddress', 'Registered Office Address', { multiline: true }],
    ['registeredOfficeCity', 'City'],
    ['registeredOfficeState', 'State'],
    ['registeredOfficePinCode', 'PIN / Postal Code', { maxLength: 10 }],
    ['registeredOfficeCountry', 'Country'],
    ['officePhoneNumber', 'Office Phone Number', { type: 'tel', maxLength: 20 }],
    ['officialEmailAddress', 'Official Email Address', { type: 'email' }],
  ] },
  { title: 'Organization & Team', fields: [
    ['totalNumberOfEmployees', 'Total Number of Employees', { type: 'number', min: 0, max: 2147483647 }],
    ['numberOfBranches', 'Number of Branches', { type: 'number', min: 0, max: 2147483647 }],
    ['numberOfOfficeLocations', 'Number of Office Locations', { type: 'number', min: 0, max: 2147483647 }],
    ['numberOfTeams', 'Number of Teams', { type: 'number', min: 0, max: 2147483647 }],
    ['departments', 'Departments', { multiline: true }],
  ] },
  { title: 'Policy Details', fields: [
    ['payrollCycle', 'Payroll Cycle'],
    ['salaryDisbursementDate', 'Salary Disbursement Date'],
    ['officeTimings', 'Office Timings'],
    ['shiftTimings', 'Shift Timings'],
    ['weeklyOffPattern', 'Weekly Off Pattern'],
    ['attendanceCaptureMethod', 'Attendance Capture Method', { choices: ['Biometric', 'Mobile App', 'Manual', 'RFID'] }],
    ['holidayList', 'Holiday List', { multiline: true }],
    ['leavePolicy', 'Leave Policy', { multiline: true }],
    ['leaveEligibilityRules', 'Leave Eligibility Rules', { multiline: true }],
  ] },
  { title: 'Existing Applications', fields: [
    ['existingAccountingSoftware', 'Accounting Software'],
    ['existingPayrollSoftware', 'Payroll Software'],
    ['existingHrms', 'HRMS'],
    ['existingAttendanceSystem', 'Attendance System'],
    ['existingErp', 'ERP'],
    ['existingCrm', 'CRM'],
  ] },
  { title: 'Contact Persons', fields: [
    ['ownerName', 'Owner Name'],
    ['ownerEmail', 'Owner Email', { type: 'email' }],
    ['ownerPhone', 'Owner Phone', { type: 'tel', maxLength: 15 }],
    ['hrPocName', 'HR Point of Contact Name'],
    ['hrPocEmail', 'HR Point of Contact Email', { type: 'email' }],
    ['hrPocPhone', 'HR Point of Contact Phone', { type: 'tel', maxLength: 15 }],
  ] },
];

export default function RegistrationForm({ onBack }) {
  const [values, setValues] = useState({});
  const [section, setSection] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [result, setResult] = useState(null);
  const [invalidField, setInvalidField] = useState(null);
  const inputs = useRef({});
  const submitting = useRef(false);

  useEffect(() => {
    if (invalidField) {
      inputs.current[invalidField]?.focus();
      inputs.current[invalidField]?.reportValidity();
      setInvalidField(null);
    }
  }, [section, invalidField]);

  async function handleSubmit(event) {
    event.preventDefault();
    if (submitting.current) return;
    setError('');
    const payload = {};
    for (const [index, group] of registrationSections.entries()) {
      for (const [name, label, options = {}] of group.fields) {
        const value = (values[name] || '').trim();
        const input = inputs.current[name];
        input.setCustomValidity(options.required && !value ? `${label} is required.` : '');
        if (!input.checkValidity()) {
          setSection(index);
          setInvalidField(name);
          setError(`Please check ${label} in ${group.title}.`);
          return;
        }
        // Optional constrained strings must be null, not empty strings, for Bean Validation.
        payload[name] = value === '' ? null : options.type === 'number' ? Number(value) : value;
      }
    }
    submitting.current = true;
    setLoading(true);
    try {
      setResult(await api.register(payload));
    } catch (err) {
      setError(err.message || 'Unable to submit your registration. Please try again.');
    } finally {
      submitting.current = false;
      setLoading(false);
    }
  }

  if (result) {
    return (
      <div className="lp-card lp-registration">
        <h2 className="lp-title">Registration Submitted</h2>
        <div className="lp-success" role="status">
          <div>
            <p>{result.message || 'Your registration request was submitted successfully.'}</p>
            {result.data?.referenceNumber != null && <p>Reference number: <strong>{result.data.referenceNumber}</strong></p>}
          </div>
        </div>
        <button type="button" className="lp-submit" onClick={onBack}>Back to Login Page</button>
      </div>
    );
  }

  return (
    <div className="lp-card lp-registration">
      <h2 className="lp-title">Register Your Organization</h2>
      <p className="lp-subtitle">Tell us about your organization. Fields marked * are required.</p>
      {error && <div className="lp-error" role="alert">{error}</div>}
      <form onSubmit={handleSubmit} noValidate aria-busy={loading}>
        {registrationSections.map((group, index) => (
          <section key={group.title} id={`registration-section-${index}`} hidden={section !== index}
            aria-labelledby={`registration-heading-${index}`}>
            <h3 id={`registration-heading-${index}`}>{group.title}</h3>
            <div className="lp-registration-fields">
              {group.fields.map(([name, label, options = {}]) => {
                const { multiline, choices, ...attributes } = options;
                const Control = choices ? 'select' : multiline ? 'textarea' : 'input';
                return (
                  <div className={`lp-field${multiline ? ' lp-registration-wide' : ''}`} key={name}>
                    <label htmlFor={`register-${name}`}>{label}{options.required ? ' *' : ''}</label>
                    <Control {...attributes} id={`register-${name}`} name={name}
                      ref={(element) => { inputs.current[name] = element; }}
                      {...(choices ? {} : multiline ? { rows: 3 } : { type: options.type || 'text', ...(options.type === 'number' ? { step: 1 } : {}) })}
                      value={values[name] || ''} disabled={loading}
                      onChange={(event) => {
                        event.target.setCustomValidity('');
                        setValues((previous) => ({ ...previous, [name]: event.target.value }));
                      }}>
                      {choices ? <>
                        <option value="">Select {label}</option>
                        {choices.map((choice) => <option key={choice} value={choice}>{choice}</option>)}
                      </> : undefined}
                    </Control>
                  </div>
                );
              })}
            </div>
          </section>
        ))}
        <div className="lp-registration-paging">
          <button type="button" className="lp-otp-btn" disabled={loading || section === 0}
            onClick={() => setSection(section - 1)}>Previous</button>
          <span>Section {section + 1} of {registrationSections.length}</span>
          <button type="button" className="lp-otp-btn" disabled={loading || section === registrationSections.length - 1}
            onClick={() => setSection(section + 1)}>Next</button>
        </div>
        <div className="lp-registration-actions">
          <button type="submit" className="lp-submit" disabled={loading}>
            {loading ? 'Submitting...' : 'Submit'}
          </button>
          <button type="button" className="lp-submit" disabled={loading} onClick={onBack}>Back to Login</button>
        </div>
      </form>
    </div>
  );
}